import React, { useState } from 'react'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../../Buttons/OutlinedButton'
import { useTranslation } from 'react-i18next'
import TableHeaders from '../../Interfaces/TableHeaders'
import { Box, useTheme } from '@mui/material'
import ViewBillingInfoModal from '../../Families/ViewBillingInfoModal'
import { reinterpretYearMonthDayAsLocalTime } from '../../../utils/reinterpretYearMonthDayAsLocalTime'
import EnrollmentFeeInfoModal from '../../Modals/EnrollmentFeeInfoModal'
import type { TFunction } from 'i18next'
import CardFormHeader from '../../Card/CardFormHeader'
import AmountDue from './AmountDue'
import Header, { HeaderVariant } from '../../Elements/Header'
import TransactionModal from '../../Modals/TransactionModal'
import { Payment } from '../../Interfaces/Payment'
import { getCommaSeparatedList } from '../../../utils/getCommaSeparatedList'
import ProgramOption from '../../Interfaces/ProgramOption'
import { dateToDashString, IS_UTC } from '../../../utils/dateUtility'
import { PaginationResponse } from '../../../swagger'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../../Table/ActionableTable'
import { Action } from '../../Table/RowActions'
import getLocaleCurrencyForAmount from '../../../utils/getLocaleCurrencyForAmount'
import { useAuth } from '../../Routes/AuthProvider'

export enum BillingHistoryType {
  Invoice = 'Invoice',
  Payment = 'Payment',
}

export enum BillingHistorySummaryTableVariant {
  /**
   * Shows a Program Director their history of `Licensing Payments` made to CC Home Office.
   *
   * * Visible when logged in as a Program Director
   * * Table located in UI under Account > Billing as "Licensing Payment History"
   */
  LicensingPayments = 'licensingPayments',
  /**
   * Shows a CSR/Admin a director's history of `Licensing Payments` made to CC Home Office.
   *
   * * Visible when logged in as a Program Director
   * * Table located in UI under Account > Billing as "Licensing Payment History"
   */
  LicensingPaymentsCSR = 'licensingPaymentsCSR',
  /**
   * Shows a Parent their history of `Enrollment Payments` made to a Program Director.
   *
   * * Visible when logged in as a Parent
   * * Table located in UI under Account > Billing as "Transactions"
   */
  EnrollmentPayments = 'enrollmentPayments',
  /**
   * Shows a CSR/Admin a parent's history of `Enrollment Payments` made to a Program Director.
   *
   * * Visible when logged in as a CSR
   * * Table located in UI under User Accounts > User Details as "Enrollment Payment Transactions"
   */
  EnrollmentPaymentsCSR = 'enrollmentPaymentsCSR',
}

export interface Transaction {
  paymentMethod: string
  date: Date
  status: string
  amount: string
  orderId: string
  parentId: number
  studentNames?: string[]
}

interface BillingHistorySummaryTableProps {
  /**
   * Variant of the BillingHistorySummaryTable to be rendered. Determines the
   * fields, headers, and more.
   * @type BillingHistorySummaryTableVariant
   */
  variant: BillingHistorySummaryTableVariant
  /**
   * optional aria-labelledby tag provided to the table to be labelled by a
   * header in another component.
   * @type string
   */
  ariaLabelledBy?: string
  /**
   * List of payments made to be displayed within the rendered table. If there
   * is no history, 'No previous transactions' is displayed
   * @type Payment Array
   */
  paymentHistory: Payment[]
  /**
   * The amount due to display above the table.
   * @type number
   */
  amountDue: string
  /**
   * User key provided to add and edit transaction modals. Required when making
   * edits to payments or starting a new payment.
   * @type number
   */
  userKey?: number
  /**
   * Available programs when adding or editing a transaction for enrollments
   * @type ProgramOption Array
   */
  enrollmentProgramOptions?: ProgramOption[]
  /**
   * Available programs when adding or editing a transaction for licensing
   * @type ProgramOption Array
   */
  licensingProgramOptions?: ProgramOption[]
  /**
   * A callback method to update the billing history when a change occurs
   * @type function
   */
  triggerRefresh?: () => void
  /**
   * Determines whether a user has permission to see information pertaining to adding and editing payments
   * Defaults to false
   * @type boolean
   */
  hasPermissionToFetchPaymentOptions?: boolean
  /**
   * Detailed licensing history
   * @type Payment[]
   */
  licensingPaymentHistory?: Payment[]

  isTableHeaderVisible?: boolean
}

export const getHeadersForVariant = (
  variant: BillingHistorySummaryTableVariant,
  t: TFunction
): TableHeaders[] => {
  const dateHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Date.Header', 'Date'),
    align: 'left',
  }
  const amountHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Amount.Header', 'Amount'),
    align: 'right',
  }
  const emptyHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Empty.Header', ''),
    align: 'right',
  }
  const communityNameHeader: TableHeaders = {
    label: t(
      'BillingHistorySummaryTable.CommunityName.Header',
      'Community Name'
    ),
    align: 'left',
  }
  const communityKeyHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.CommunityKey.Header', 'Community Id'),
    align: 'left',
  }
  const programHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Program.Header', 'Program'),
    align: 'left',
  }
  const descriptionHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Header.Description', 'Description'),
    align: 'left',
  }
  const paymentMethodHeader: TableHeaders = {
    label: t(
      'BillingHistorySummaryTable.Header.PaymentMethod',
      'Payment Method'
    ),
    align: 'left',
  }
  const acumaticaOrderNumberHeader: TableHeaders = {
    label: t(
      'BillingHistorySummaryTable.Header.AcumaticaOrderNumber',
      'Acumatica Order #'
    ),
    align: 'left',
  }
  const statusHeader: TableHeaders = {
    label: t('BillingHistorySummaryTable.Header.Status', 'Status'),
    align: 'right',
  }

  switch (variant) {
    case BillingHistorySummaryTableVariant.LicensingPayments:
      return [
        dateHeader,
        descriptionHeader,
        communityNameHeader,
        communityKeyHeader,
        programHeader,
        paymentMethodHeader,
        acumaticaOrderNumberHeader,
        amountHeader,
        statusHeader,
      ]
    case BillingHistorySummaryTableVariant.LicensingPaymentsCSR:
      return [
        dateHeader,
        descriptionHeader,
        communityNameHeader,
        communityKeyHeader,
        programHeader,
        paymentMethodHeader,
        acumaticaOrderNumberHeader,
        amountHeader,
        statusHeader,
        emptyHeader,
      ]
    case BillingHistorySummaryTableVariant.EnrollmentPayments:
    case BillingHistorySummaryTableVariant.EnrollmentPaymentsCSR:
    default:
      return [
        dateHeader,
        descriptionHeader,
        programHeader,
        paymentMethodHeader,
        amountHeader,
        statusHeader,
        emptyHeader,
      ]
  }
}

export const generateKeyForProgramOptionsMap = (
  program: number,
  studentKey: number
): string => {
  return `${program}-${studentKey}`
}

const BillingHistorySummaryTable: React.FC<BillingHistorySummaryTableProps> = ({
  variant,
  ariaLabelledBy,
  paymentHistory,
  amountDue,
  licensingProgramOptions,
  enrollmentProgramOptions,
  userKey,
  triggerRefresh,
  hasPermissionToFetchPaymentOptions,
  isTableHeaderVisible = true,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { permissionAbility, userDetails } = useAuth()

  const canUpdateEnrollment = permissionAbility.can(
    'updateEnrollmentPayment',
    'Payment'
  )
  const canUpdateLicensing = permissionAbility.can(
    'updateLicensingPayment',
    'Payment'
  )
  const canUpdateLicensingDownline = permissionAbility.can(
    'updateLicensingPaymentOnlyDownline',
    'Payment'
  )

  const canEditPayments =
    canUpdateEnrollment || canUpdateLicensing || canUpdateLicensingDownline

  const canShowPaymentStatusSelector = permissionAbility.can(
    'updatePaymentStatus',
    'Payment'
  )

  const isLicensingVariant = variant.includes('licensing')

  /** State Objects */
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const [currencyCode] = useState('USD')
  const [billingHistoryViewModalOpen, setBillingHistoryViewModalOpen] =
    useState(false)
  const [isEnrollmentFeeInfoModalOpen, setIsEnrollmentFeeInfoModalOpen] =
    useState(false)
  // TODO: Make this value dynamic (See CCP1-2487)
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false)

  const [transaction, setTransaction] = useState<Transaction>({
    paymentMethod: '',
    date: new Date(),
    status: '',
    amount: '',
    orderId: '',
    parentId: -1,
    studentNames: [''],
  })

  const [selectedPayment, setSelectedPayment] = useState<Payment | undefined>()
  const [isEditClicked, setIsEditClicked] = useState(false)
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([])

  /** Methods */

  const handleOnClose = () => {
    setSelectedPayment(undefined)
    setSelectedPrograms([])
    setIsEditClicked(false)
    setIsTransactionModalOpen((isOpen) => !isOpen)
    triggerRefresh?.()
  }

  const billingHistoryViewModalOnClose = () => {
    setBillingHistoryViewModalOpen(false)
  }

  const onCloseEnrollmentFeeInfoModal = () => {
    setIsEnrollmentFeeInfoModalOpen(false)
  }

  const handleChangePage = (pagination: PaginationResponse) => {
    setPage(pagination.page)
    setPageSize(pagination.pageSize)
  }

  const handleMoreInfo = (
    paymentMethod: string,
    date: Date,
    status: string,
    amount: string,
    orderId: string,
    parentKey: number,
    studentNames: string[]
  ) => {
    setTransaction({
      paymentMethod,
      date,
      status,
      amount,
      orderId,
      parentId: parentKey,
      studentNames,
    })
    setBillingHistoryViewModalOpen(true)
  }

  const handleEdit = (payment: Payment) => {
    setSelectedPayment(payment)
    setIsEditClicked((isClicked) => !isClicked)
    setSelectedPrograms(
      isLicensingVariant
        ? licensingProgramOptions?.filter(
            (licensingProgramOption) =>
              payment.paymentsMade[0].programKey ===
              licensingProgramOption.programKey
          ) ?? []
        : enrollmentProgramOptions?.filter((enrollmentProgramOption) => {
            return payment.paymentsMade.some((paymentMade) => {
              return (
                paymentMade.programKey === enrollmentProgramOption.programKey &&
                paymentMade.studentKey === enrollmentProgramOption.studentKey
              )
            })
          }) ?? []
    )
    setIsTransactionModalOpen((isOpen) => !isOpen)
  }

  const AdditionalTableHeader = (
    variant: BillingHistorySummaryTableVariant
  ) => {
    switch (variant) {
      case BillingHistorySummaryTableVariant.EnrollmentPaymentsCSR:
        return (
          <CardFormHeader
            header={
              <Box margin={theme.spacing(2, 3, 0, 2)}>
                <Header
                  id="enrollmentPaymentTransactionsTableHeader"
                  headerName={t(
                    'EnrollmentPaymentTransactionsCard.Header.MembershipPaymentTransactions',
                    'Membership Payment Transactions'
                  )}
                  component="h3"
                  variant={HeaderVariant.Card}
                />
                <AmountDue amountDue={amountDue} currencyCode={currencyCode} />
              </Box>
            }
            buttons={
              hasPermissionToFetchPaymentOptions && (
                <Box mr={3} mt={3}>
                  <OutlinedButton
                    id="addTransaction"
                    variant={OutlinedButtonVariant.AddTransaction}
                    onClick={() =>
                      setIsTransactionModalOpen((isOpen) => !isOpen)
                    }
                  />
                </Box>
              )
            }
            headerContainerProps={{ marginLeft: 0 }}
          />
        )
      case BillingHistorySummaryTableVariant.LicensingPaymentsCSR:
        return (
          <Box margin={theme.spacing(2, 3, 0, 2)}>
            <CardFormHeader
              header={
                <Box>
                  <Header
                    id="licensingPaymentTransactionsTableHeader"
                    headerName={t(
                      'LicensingPaymentTransactionsCard.Header.LicensingPaymentTransactions',
                      'Licensing Payment History'
                    )}
                    component="h3"
                    variant={HeaderVariant.Card}
                  />
                  <AmountDue
                    amountDue={amountDue}
                    currencyCode={currencyCode}
                  />
                </Box>
              }
              buttons={
                hasPermissionToFetchPaymentOptions ? (
                  <Box mr={3} mt={3}>
                    <OutlinedButton
                      id="addLicensingTransaction"
                      variant={OutlinedButtonVariant.AddTransaction}
                      onClick={() =>
                        setIsTransactionModalOpen((isOpen) => !isOpen)
                      }
                    />
                  </Box>
                ) : null
              }
              headerContainerProps={{ marginLeft: 0 }}
            />
          </Box>
        )
      case BillingHistorySummaryTableVariant.LicensingPayments:
      default:
        return null
    }
  }

  const getProgramListing = (
    variant: BillingHistorySummaryTableVariant,
    record: Payment
  ) => {
    return variant.includes('licensing')
      ? /**
         * If we're showing licensing details for a payment made for a given program,
         * use the licensing payment provided to show ProgramType (SemesterOneStartDate)
         */
        `${record.licensingPayment?.programType ?? ''} (${
          record.licensingPayment?.semesterOneStartDate
            ? dateToDashString(
                record.licensingPayment?.semesterOneStartDate as Date,
                IS_UTC
              )
            : ''
        })`
      : /**
         * Otherwise, we're showing the enrollments as a comma separated list.
         */
        getCommaSeparatedList(
          record.enrollmentPayments?.map(
            (payment) =>
              `${payment.studentFirstName} - ${payment.programType ?? ''} (${
                payment.semesterOneStartDate
                  ? dateToDashString(payment.semesterOneStartDate, IS_UTC)
                  : ''
              })`
          ) ?? []
        )
  }
  /** Other Objects */
  const actionableTableColumnsHeader: ActionableTableColumn[] =
    variant.includes('licensing')
      ? [
          {
            fieldName: 'date',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Date',
              'Date'
            ),
          },
          {
            fieldName: 'desc',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Description',
              'Description'
            ),
          },
          {
            fieldName: 'communityName',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.CommunityName',
              'Community Name'
            ),
          },
          {
            fieldName: 'communityId',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.CommunityId',
              'Community Id'
            ),
          },
          {
            fieldName: 'program',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Program',
              'Program'
            ),
          },
          {
            fieldName: 'paymentMethodKey',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.PaymentMethod',
              'Payment Method'
            ),
          },
          {
            fieldName: 'acumaticaOrderNumber',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.AcumaticaOrderNumber',
              'Acumatica Order #'
            ),
          },
          {
            fieldName: 'paymentAmount',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Amount',
              'Amount'
            ),
          },
          {
            fieldName: 'paymentStatusKey',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Status',
              'Status'
            ),
          },
          {
            fieldName: 'paidBy',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.PaidBy',
              'Paid by'
            ),
          },
        ]
      : [
          {
            fieldName: 'date',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Date',
              'Date'
            ),
          },
          {
            fieldName: 'desc',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Description',
              'Description'
            ),
          },
          {
            fieldName: 'program',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Program',
              'Program'
            ),
            renderCell: (params) => {
              const formattedText = params.row.program
                ?.split(', ') // Split at commas
                .map((entry: string, index: number) => (
                  <Box key={index} component="span" display="block">
                    {entry}
                  </Box>
                ))

              return (
                <Box
                  className="MuiDataGrid-cellContent"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formattedText}
                </Box>
              )
            },
            minWidth: 320,
          },
          {
            fieldName: 'paymentMethodKey',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.PaymentMethod',
              'Payment Method'
            ),
          },
          {
            fieldName: 'paymentAmount',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Amount',
              'Amount'
            ),
          },
          {
            fieldName: 'paymentStatusKey',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.Status',
              'Status'
            ),
          },
          {
            fieldName: 'paidBy',
            columnHeaderName: t(
              'BillingHistorySummaryTable.Header.PaidBy',
              'Paid by'
            ),
          },
        ]

  const rowActions: Action<Payment>[] = [
    {
      actionName: t(
        'Billing.BillingHistorySummaryTable.RowAction.Edit',
        'Edit'
      ),
      actionKey: 'edit',
      actionFunction: (payment) => handleEdit(payment),
      hide: (payment) => {
        // If the user doesn't have permissions to edit any payments, hide the edit button entirely
        if (!canEditPayments) return true

        // If the user can edit all payments, show the edit button for all rows
        if (canUpdateLicensing) return false

        // if the user can only edit downline licensing payments, only show the edit button for payments they can edit
        return isLicensingVariant
          ? !payment?.canUserEdit
          : payment?.userKey !== userDetails.userKey
      },
    },
    {
      actionName: t(
        'Billing.BillingHistorySummaryTable.RowAction.MoreInfo',
        'More Info'
      ),
      actionKey: 'moreInfo',
      actionFunction: (record) => {
        handleMoreInfo(
          record.paymentMethodKey,
          record.begunAt,
          record.paymentStatusKey,
          record.total?.amount.toString() ?? '',
          record.orderId ?? '',
          record.userKey,
          record.enrollmentPayments?.map(
            (it) => it?.studentFirstName as string
          ) ?? []
        )
      },
      hide: () => !canEditPayments,
    },
  ]

  const rows: Payment[] = paymentHistory.map((payment) => {
    return {
      ...payment,
      date: dateToDashString(payment.begunAt, IS_UTC),
      program: getProgramListing(variant, payment),
      paymentAmount: getLocaleCurrencyForAmount(
        payment.amount.amount,
        payment.amount.currencyCode
      ),
      communityName: payment.licensingPayment?.communityName,
      communityId: payment.licensingPayment?.communityKey,
      acumaticaOrderNumber: payment.orderId,
      paidBy: payment.paidBy || '',
    }
  })

  return (
    <>
      {/* 
        Either we clicked edit and await our selected payment information
        OR we have clicked Add Transaction and don't need to wait.
      */}
      {(isEditClicked || (!isEditClicked && isTransactionModalOpen)) && (
        <TransactionModal
          userKey={userKey}
          isLicensingVariant={isLicensingVariant}
          isOpen={isTransactionModalOpen}
          onClose={handleOnClose}
          programOptions={
            isLicensingVariant
              ? licensingProgramOptions ?? []
              : enrollmentProgramOptions ?? []
          }
          payment={selectedPayment}
          selectedPrograms={selectedPrograms}
          canShowPaymentStatusSelector={canShowPaymentStatusSelector}
        />
      )}
      <ViewBillingInfoModal
        transactionType={transaction.paymentMethod}
        date={reinterpretYearMonthDayAsLocalTime(transaction.date)}
        status={transaction.status}
        amount={transaction.amount}
        orderId={transaction.orderId}
        students={transaction.studentNames ?? []}
        isOpen={billingHistoryViewModalOpen}
        onClose={billingHistoryViewModalOnClose}
      />
      <EnrollmentFeeInfoModal
        isOpen={isEnrollmentFeeInfoModalOpen}
        onClose={onCloseEnrollmentFeeInfoModal}
      />
      {isTableHeaderVisible && AdditionalTableHeader(variant)}
      <ActionableTable
        columns={actionableTableColumnsHeader}
        rows={rows}
        noResultsMessage={t(
          'Billing.BillingHistorySummaryTable.Table.NoPreviousTransactions',
          'No previous transactions'
        )}
        ariaLabelledBy={ariaLabelledBy}
        rowActions={rowActions}
        customCssTable={{
          paddingLeft: theme.spacing(0.8),
          height: theme.spacing(14),
          ...(!rows.length
            ? {
                '& .MuiBox-root': {
                  alignItems: 'flex-start',
                  padding: `${theme.spacing(0.6)} ${theme.spacing(1.5)}`,
                },
              }
            : {}),
        }}
        pagination={{
          page,
          pageSize,
          totalCount: rows.length,
          orderBy: [{}],
        }}
        handlePaginationChange={handleChangePage}
      />
    </>
  )
}

export default BillingHistorySummaryTable
